import { useState, useEffect, useCallback } from 'react';
import { Session } from '../api/types';
import { parseServerDate, formatClock } from '../utils/dateUtils';

/**
 * The crawl clock. Ticks every second while the session is live; freezes on
 * the recorded end time once the session is finalised or complete. The
 * end-time selection and parseServerDate handling are behaviour-critical —
 * lifted as-is from the old PubList component.
 */
export const useElapsedTime = (session: Session | null): string => {
  const [elapsedTime, setElapsedTime] = useState('');

  const calculateElapsedTime = useCallback(() => {
    if (!session) {
      setElapsedTime('');
      return;
    }

    const startTime = parseServerDate(session.sessionStartTime);

    if (isNaN(startTime.getTime())) {
      setElapsedTime('');
      return;
    }

    // If session is finalised, use the end time instead of current time
    let endTime: Date;
    if ((session.isFinalised || session.isComplete) && session.sessionEndTime) {
      endTime = parseServerDate(session.sessionEndTime);
    } else {
      endTime = new Date();
    }

    const diffInSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
    setElapsedTime(formatClock(diffInSeconds));
  }, [session]);

  useEffect(() => {
    calculateElapsedTime();

    // Only set up interval if session is not finalised
    if (!session?.isFinalised && !session?.isComplete) {
      const interval = setInterval(() => {
        calculateElapsedTime();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [calculateElapsedTime, session?.isComplete, session?.isFinalised]);

  return elapsedTime;
};
